import React, { useRef } from 'react';
import type { PhotoScore } from '@frame/scoring-types';

const C = {
  teal: '#50B8A8', text: '#f8fafc', secondary: '#94a3b8',
  surface: '#1e293b', divider: '#334155', bg: '#0f172a',
  navySoft: '#2D4A66', success: '#059669',
} as const;

function scoreColor(score: number): string {
  if (score < 3) return '#EF4444';
  if (score < 5) return '#F59E0B';
  if (score < 7) return '#50B8A8';
  return '#059669';
}

interface PhotoStripProps {
  scoredPhotos: PhotoScore[];
  unratedPhotos: { file: File; url: string }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAddUnrated: (files: FileList) => void;
}

export function PhotoStrip({ scoredPhotos, unratedPhotos, selectedId, onSelect, onAddUnrated }: PhotoStripProps) {
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      overflowX: 'auto', padding: '8px 0',
      scrollbarWidth: 'thin',
    }}>
      {scoredPhotos.map(p => (
        <button
          key={p.id}
          onClick={() => onSelect(p.id)}
          style={{
            flexShrink: 0, width: 64, height: 64, borderRadius: 10,
            border: selectedId === p.id ? `2px solid ${C.teal}` : `2px solid transparent`,
            padding: 0, cursor: 'pointer', position: 'relative',
            overflow: 'hidden', background: C.surface,
          }}
        >
          {p.thumbnailUri && (
            <img src={p.thumbnailUri} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          <span style={{
            position: 'absolute', bottom: 2, right: 2,
            fontSize: 10, fontWeight: 700,
            color: '#fff', background: scoreColor(p.compositeScore),
            padding: '1px 4px', borderRadius: 4,
          }}>
            {p.compositeScore.toFixed(1)}
          </span>
        </button>
      ))}

      {unratedPhotos.map((u, i) => (
        <button
          key={`unrated-${i}`}
          onClick={() => onSelect(`unrated-${i}`)}
          style={{
            flexShrink: 0, width: 64, height: 64, borderRadius: 10,
            border: selectedId === `unrated-${i}` ? `2px solid ${C.teal}` : `2px solid transparent`,
            padding: 0, cursor: 'pointer', position: 'relative',
            overflow: 'hidden', background: C.surface,
          }}
        >
          <img src={u.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <span style={{
            position: 'absolute', bottom: 2, right: 2,
            fontSize: 9, fontWeight: 600,
            color: C.secondary, background: 'rgba(0,0,0,0.6)',
            padding: '1px 4px', borderRadius: 4,
          }}>
            N/A
          </span>
        </button>
      ))}

      {/* Add unrated button */}
      <button
        onClick={() => fileRef.current?.click()}
        style={{
          flexShrink: 0, width: 64, height: 64, borderRadius: 10,
          border: `2px dashed ${C.divider}`,
          background: 'transparent', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: C.secondary, fontSize: 24,
        }}
        title="Add unrated photos"
      >
        +
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files?.length) onAddUnrated(e.target.files); }}
      />
    </div>
  );
}
